// @flow weak

import PropTypes from 'prop-types';
import React from 'react';
// !!! IMPORTANT !!!
//
// This file is shared between the zapier/zapier and zapier/next-js repository.
// Any changes made here should be reflected in there as well.
// For more information, see: https://zapier.quip.com/xaKqAOpdhpY2#ZXFACA8Jnp9
import _ from 'lodash';
import createReactClass from 'create-react-class'; // eslint-disable-line no-restricted-syntax
import memoize from 'memoize-weak';
import bemify, { toModifiers } from 'bemify-js';

import { AccessibleElement } from '@zapier/legacy-components';
import { ensureArray } from '@zapier/common-utils';
import { Icon, TruncatedText } from '@zapier/common-components';

import Link from 'web-ssr/app/common/components/Link';
import ChoiceList, {
  ChoiceListValuePropType,
} from 'web-ssr/app/common/components/ChoiceList';
import 'web-ssr/app/common/components/Menu/Menu.scss';

const bem = bemify('menu');

// Default component that is rendered within a `Menu`.
// Can be replaced by any other component. This one will
// either be a `Link` or a `span` based on `props.href`.
const MenuItem = props => {
  const Component = props.href ? Link : 'span';
  const iconNode = props.icon && (
    <Icon className={bem('__icon')} name={props.icon} />
  );
  const iconAfterNode = props.iconAfter && (
    <Icon className={bem('__icon --after')} name={props.iconAfter} />
  );
  const className = bem(
    '__label',
    toModifiers(props.kind, {
      disabled: props.isDisabled,
    }),
    props.className
  );
  const componentProps = !props.href ? {} : { href: props.href };
  return (
    <Component {...componentProps} className={className}>
      <div className={bem('__label-content')}>
        {iconNode}
        <span className={bem('__label-text')}>
          <TruncatedText isBlock={true}>{props.label}</TruncatedText>
        </span>
        {iconAfterNode}
      </div>
    </Component>
  );
};

MenuItem.propTypes = {
  href: PropTypes.string,
  icon: PropTypes.string,
  iconAfter: PropTypes.string,
  label: PropTypes.string.isRequired,
  onClick: PropTypes.func,
};

const itemsPropType = PropTypes.arrayOf(
  PropTypes.shape({
    ...MenuItem.propTypes, // eslint-disable-line react/forbid-foreign-prop-types
    value: ChoiceListValuePropType,
  })
);

// `ChoiceList` requires that `items` have `value` keys
// and I still think it's good to require those, so add
// them here.
const ensureValue = memoize(item =>
  item.value !== undefined
    ? item
    : {
        ...item,
        value: item.label,
      }
);

const MenuList = props => (
  <ChoiceList
    className={bem('__list', toModifiers(props.kind), props.className)}
    hasCustomChoiceHandlers={props.onChoose === MenuList.defaultProps.onChoose}
    Item={props.MenuItem}
    itemClassName={bem('__item')}
    items={props.items.map(_.ary(ensureValue, 1))}
    onChoose={props.onChoose}
  />
);

MenuList.propTypes = {
  MenuItem: PropTypes.func.isRequired,
  items: itemsPropType.isRequired,
  onChoose: PropTypes.func,
};

MenuList.defaultProps = {
  onChoose: () => {},
};

export const MenuSectionShape = {
  children: (props, propName, componentName) => {
    const children = props[propName];
    return !(children && props.items)
      ? undefined
      : new Error(
          `
      'children' and 'items' supplied to '${componentName}',
      but only one can be supplied and rendered
    `.replace(/\s{2,}/g, ' ')
        );
  },
  headerKind: PropTypes.string,
  icon: PropTypes.string,
  isOpen: PropTypes.bool,
  items: itemsPropType,
  listClassName: PropTypes.string,
  listKind: PropTypes.string,
  MenuItem: PropTypes.func,
  shouldRenderCount: PropTypes.bool,
  title: PropTypes.string,
  onChoose: PropTypes.func,
  onToggleIsOpen: PropTypes.func,
};

export const MenuSection = props => {
  const hasList = !props.children && !!props.items;
  const canToggle = !!props.onToggleIsOpen;
  const icon = !props.icon ? null : (
    <Icon className={bem('__section-header-icon')} name={props.icon} />
  );
  const toggleIconClassName = bem('__section-header-toggle-icon', {
    '--open': props.isOpen,
  });
  const toggleIcon = !canToggle ? null : (
    <Icon className={toggleIconClassName} name="arrow-right" size={12} />
  );
  const headerClassName = bem(
    '__section-header',
    {
      '--interactive': canToggle,
    },
    toModifiers(props.headerKind)
  );
  const count = !(props.shouldRenderCount && hasList) ? null : (
    <div className={bem('__section-header-count')}>{props.items.length}</div>
  );
  const header = !props.title ? null : (
    <AccessibleElement.Header
      className={headerClassName}
      onClick={props.onToggleIsOpen}
    >
      {icon}
      <span className={bem('__section-header-text')}>
        <TruncatedText isBlock={false}>{props.title}</TruncatedText>
      </span>
      {count}
      {toggleIcon}
    </AccessibleElement.Header>
  );
  const contentClassName = bem('__section-content', {
    '--list': hasList,
  });
  const maybeList =
    !props.items || props.items.length === 0 ? null : (
      <MenuList
        className={props.listClassName}
        items={props.items}
        MenuItem={props.MenuItem}
        onChoose={props.onChoose}
      />
    );
  const contentChildren = hasList ? maybeList : props.children;
  const content =
    !props.isOpen || !contentChildren ? null : (
      <div className={contentClassName}>{contentChildren}</div>
    );
  return (
    <section className={bem('__section', props.className)}>
      {header}
      {content}
    </section>
  );
};

MenuSection.propTypes = MenuSectionShape;

MenuSection.defaultProps = {
  MenuItem,
  isOpen: true,
  shouldRenderCount: false,
};

export const defaultIsSectionOpen = (id, openIds) => _.includes(openIds, id);

export const MenuSectionsStateful = createReactClass({
  displayName: 'MenuSectionsStateful',

  propTypes: {
    children: PropTypes.any.isRequired,
    canToggleSections: PropTypes.bool.isRequired,
    defaultOpenId: PropTypes.string,
    isSectionOpen: PropTypes.func.isRequired,
    listClassName: MenuSectionShape.listClassName,
    listKind: MenuSectionShape.listKind,
    onChoose: MenuSectionShape.onChoose,
  },

  getDefaultProps() {
    return {
      canToggleSections: true,
      isSectionOpen: defaultIsSectionOpen,
    };
  },

  getInitialState() {
    const { defaultOpenId } = this.props;
    return { openMenuIds: defaultOpenId ? ensureArray(defaultOpenId) : [] };
  },

  onToggleMenu(menuId) {
    this.setState({ openMenuIds: [menuId] });
  },

  render() {
    const { props } = this;
    const renderableChildren = React.Children.toArray(props.children).filter(
      Boolean
    );
    // Prevent toggle controls from rendering when there's only one child
    const hasMultipleChildren = renderableChildren.length > 1;
    const children = renderableChildren.map(child => {
      const id = _.get(child, ['props', 'id']);
      return React.cloneElement(child, {
        isOpen:
          !hasMultipleChildren ||
          props.isSectionOpen(id, this.state.openMenuIds),
        listClassName: props.listClassName,
        listKind: props.listKind,
        onChoose: props.onChoose,
        onToggleIsOpen:
          !props.canToggleSections || !hasMultipleChildren
            ? null
            : () => this.onToggleMenu(id),
      });
    });
    return <div className={bem('__sections')}>{children}</div>;
  },
});

export const Menu = ({ className, kind, ...props }) => {
  const content = props.children ? (
    React.Children.map(
      props.children,
      child =>
        child &&
        React.cloneElement(child, {
          isSectionOpen: props.isSectionOpen,
          listClassName: props.listClassName,
          listKind: props.listKind,
          onChoose: props.onChoose,
        })
    )
  ) : (
    <MenuSection {...props} />
  );
  return <div className={bem(toModifiers(kind), className)}>{content}</div>;
};

// Essentially the same propTypes as `MenuSection`.
// If `children` are passed, they should all be `MenuSection`s.
// This is rather difficult to write a propType checker for.
Menu.propTypes = {
  ...MenuSectionShape,
  isSectionOpen: PropTypes.func,
};

export default Menu;
